import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  TrendingUp,
  Users,
  Target,
  Award,
  BarChart3,
  Shield,
  CheckCircle2,
  Star,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojpnneg';

const Home = () => {
  const [blogs, setBlogs] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [auditForm, setAuditForm] = useState({
    name: '',
    brand_name: '',
    phone: '',
    email: '',
    monthly_revenue: '',
    platform: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBlogs();
    fetchCaseStudies();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching case studies:', error);
    }
  };


  const testimonials = [
    {
      brand: 'Gw Khamkar',
      text: 'Working with Obiquiste Solutions has significantly improved our marketplace performance. Their PPC management and account handling brought better visibility and consistent sales growth. After A+ content and SEO optimization, we saw a noticeable boost in conversions. Highly professional team.',
      rating: 5,
    },
    {
      brand: 'Ehop',
      text: 'We partnered with Obiquiste Solutions for account management and listing optimization. Within a few months, our sales numbers improved and ad performance became much more efficient. Their structured PPC campaigns and A+ implementation made a real difference.',
      rating: 5,
    },
    {
      brand: 'Rayon',
      text: 'Obiquiste Solutions helped us streamline our Amazon account and optimize our product listings. Their SEO strategy and A+ content upgrade improved our ranking and sales velocity. The team is responsive and performance-focused.',
      rating: 5,
    },
    {
      brand: 'Haute',
      text: 'Their marketplace management approach is data-driven and result-oriented. From PPC management to content enhancement, everything was handled professionally. We experienced better traffic quality and steady revenue growth.',
      rating: 5,
    },
    {
      brand: 'Fitelo',
      text: 'As a growing brand, we needed a team that understands performance marketing deeply. Obiquiste Solutions handled our account management, PPC campaigns and A+ content seamlessly. We observed improved ad ROI and stronger marketplace presence.',
      rating: 5,
      featured: 'Shark Tank Brand',
    },
    {
      brand: 'Toyartsy',
      text: 'Our experience with Obiquiste Solutions has been excellent. Their SEO and catalog optimization improved our organic visibility, while structured PPC management helped boost our overall sales performance. Highly recommended for marketplace growth.',
      rating: 5,
    },
    {
      brand: 'Vagmine',
      text: 'They completely restructured our listings and implemented strong A+ content. With improved keyword targeting and ad strategy, our sales numbers improved steadily. Professional execution and transparent reporting.',
      rating: 5,
    },
    {
      brand: 'Miss Claire',
      text: 'Obiquiste Solutions supported us with full account management and performance marketing. Their attention to detail in SEO and PPC helped increase both traffic and conversion rate. A reliable growth partner.',
      rating: 5,
    },
    {
      brand: 'Prostuff.in',
      text: 'Their team optimized our product listings and improved ad efficiency significantly. We saw better ACOS control and improved monthly revenue. The A+ content upgrade also helped enhance brand perception.',
      rating: 5,
    },
  ];

  const handleAuditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'New Audit Request - Obiquiste Solutions',
          name: auditForm.name,
          brand_name: auditForm.brand_name,
          phone: auditForm.phone,
          email: auditForm.email,
          monthly_revenue: auditForm.monthly_revenue,
          platform: auditForm.platform,
          form_type: 'Audit Request (Home Page)'
        })
      });
      
      if (response.ok) {
        toast.success('Audit request submitted! We will contact you soon.');
        setAuditForm({
          name: '',
          brand_name: '',
          phone: '',
          email: '',
          monthly_revenue: '',
          platform: '',
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whyChooseUs = [
    {
      icon: Users,
      title: 'Marketplace Experts',
      description: '10+ years of combined experience managing top e-commerce brands across platforms.',
    },
    {
      icon: TrendingUp,
      title: 'ROI Focused Advertising',
      description: 'Data-driven campaigns that maximize returns and minimize wasteful ad spend.',
    },
    {
      icon: Award,
      title: 'Dedicated Account Managers',
      description: 'Your success is personal to us. Direct access to experienced professionals.',
    },
    {
      icon: BarChart3,
      title: 'Data-Driven Decisions',
      description: 'Advanced analytics and insights to guide every strategic move.',
    },
    {
      icon: Target,
      title: 'Catalog Optimization Experts',
      description: 'Transform listings into conversion machines with proven optimization techniques.',
    },
    {
      icon: Shield,
      title: 'Transparent Reporting',
      description: 'Real-time dashboards and weekly reports. Complete visibility into performance.',
    },
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Audit & Strategy',
      description: 'Comprehensive analysis of your current marketplace presence and competitor landscape.',
    },
    {
      step: 2,
      title: 'Catalog Optimization',
      description: 'Enhance product listings with compelling content, high-quality images, and SEO.',
    },
    {
      step: 3,
      title: 'Launch & Advertising',
      description: 'Execute strategic campaigns across platforms with continuous optimization.',
    },
    {
      step: 4,
      title: 'Scaling & Expansion',
      description: 'Expand to new marketplaces and categories while maintaining profitable growth.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Amazon & Flipkart Account Management Services India | Obiquiste Solutions</title>
        <meta name="description" content="Expert e-commerce & quick commerce services: Amazon, Flipkart, Blinkit, Swiggy Instamart account management. Trusted by 100+ brands. Get free consultation!" />
        <link rel="canonical" href="https://www.obiquistesolutions.com/" />
      </Helmet>
      <section
        data-testid="hero-section"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 163, 224, 0.03) 0%, rgba(237, 28, 36, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-secondary/5"></div>
        
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1
                data-testid="hero-heading"
                className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6"
              >
                Scale Your E-Commerce Brand Across{' '}
                <span className="text-gradient">Marketplaces</span>
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8">
                We scale brands across Amazon, Flipkart, Myntra, Ajio, Zomato, Swiggy, Swiggy Instamart and Blinkit through strategic marketplace management and performance-driven growth marketing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button
                    data-testid="hero-cta-consultation"
                    className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-8 py-6 rounded-full text-base"
                  >
                    Request Free Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid="hero-cta-services"
                    variant="outline"
                    className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold"
                  >
                    View Services
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-2xl"></div>
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/216a6a1a-ff3d-4c72-88de-4c9fd30cd839/images/5524a3f5950691b1d3114c659abf9f76c7ced57021b87884844a18dd22f08742.png"
                  alt="E-commerce Analytics Dashboard"
                  className="relative rounded-2xl shadow-2xl"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="about-section" className="py-16 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl blur-2xl opacity-50"></div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/216a6a1a-ff3d-4c72-88de-4c9fd30cd839/images/0ea5b94682a282ada84bca848c00147c95036daecb1b1f8121c3ab50fc13e007.png"
                alt="E-commerce Analytics"
                className="relative rounded-2xl shadow-2xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Built to Scale <span className="text-gradient">Marketplace Brands</span>
              </h2>
              <p className="text-lg leading-relaxed text-slate-600 mb-10">
                Obiquiste Solutions is a performance-focused ecommerce growth agency helping brands expand across Amazon, Flipkart, Myntra, Ajio, Zomato, Swiggy, Swiggy Instamart and Blinkit. From account setup to advertising optimization, we manage end-to-end marketplace growth.
              </p>
              
              <div className="grid grid-cols-1 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="group relative p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-primary/50 hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary to-primary/50 rounded-l-2xl"></div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                      <BarChart3 className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold mb-2 text-slate-900">Data-Driven Strategy</h3>
                      <p className="text-slate-600 leading-relaxed">We use analytics and marketplace insights to guide every decision.</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="group relative p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-primary/50 hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary to-secondary rounded-l-2xl"></div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                      <Target className="w-8 h-8 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold mb-2 text-slate-900">Performance Execution</h3>
                      <p className="text-slate-600 leading-relaxed">From catalog optimization to PPC campaigns, we execute with precision.</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="group relative p-8 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 hover:border-primary/50 hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-secondary to-primary/50 rounded-l-2xl"></div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                      <TrendingUp className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold mb-2 text-slate-900">Scalable Growth</h3>
                      <p className="text-slate-600 leading-relaxed">We focus on long-term brand growth across multiple marketplaces.</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="marketplaces-section" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Marketplaces We Work With
            </h2>
            <p className="text-lg text-slate-600">
              Proven expertise across India's leading e-commerce platforms
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-8 items-center">
            {[
              { name: 'Amazon', link: '/amazon-account-management-services' },
              { name: 'Flipkart', link: '/flipkart-account-management-services' },
              { name: 'Myntra', link: '/myntra-account-management-services' },
              { name: 'Ajio', link: '/ajio-account-management-services' },
              { name: 'Swiggy Instamart', link: '/swiggy-account-management' },
              { name: 'JioMart', link: '/services' },
              { name: 'Blinkit', link: '/blinkit-account-management' },
              { name: 'Swiggy', link: '/swiggy-account-management' },
              { name: 'Zomato', link: '/zomato-account-management' }
            ].map((marketplace, idx) => (
                <Link
                  key={idx}
                  to={marketplace.link}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg hover:border-primary border border-transparent transition-all cursor-pointer flex items-center justify-center min-h-[100px]"
                    data-testid={`marketplace-${idx}`}
                  >
                    <span className="font-semibold text-slate-700 text-center hover:text-primary transition-colors">{marketplace.name}</span>
                  </motion.div>
                </Link>
              )
            )}
          </div>
        </div>
      </section>

      <section data-testid="why-choose-section" className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We combine marketplace expertise with data-driven strategies to deliver exceptional
              results
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group p-8 rounded-2xl bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 transition-all hover:shadow-lg"
                data-testid={`why-choose-${idx}`}
              >
                <item.icon className="w-12 h-12 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}</div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section data-testid="brands-section" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Brands We Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We proudly manage e-commerce operations for brands across diverse categories on Amazon, Flipkart, Blinkit, and more.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[
              { name: 'Nutri Hut', category: 'Food & Nutrition' },
              { name: 'Rayon Caboki', category: 'Hair Care' },
              { name: 'Ayurdaanta Herbs', category: 'Ayurveda' },
              { name: 'Ayur Devam', category: 'Ayurveda' },
              { name: 'Suparna Trikha', category: 'Beauty' },
              { name: 'Haute', category: 'Kitchen Appliances' },
              { name: 'GK Trading', category: 'Home Décor' },
              { name: 'Yuri Woori', category: 'Beauty' },
              { name: 'Mooncntrl', category: 'Fashion' },
              { name: 'Indian Kitchen Foods', category: 'Ready-to-Eat' },
              { name: 'Earthvya', category: 'Nutraceuticals' },
              { name: 'GW Khamkar', category: 'Food & Grocery' },
              { name: 'Vintage Oils', category: 'Edible Oils' },
              { name: 'Fitelo', category: 'Smart Devices' },
              { name: 'Fitty', category: 'Supplements' },
              { name: 'RajGharana Metals', category: 'Kitchenware' },
              { name: 'HII Health Inside It', category: 'Health & Wellness' },
              { name: 'Auli Lifestyle', category: 'Skincare' },
              { name: 'EHOP', category: 'Electronics' },
              { name: 'Lavenust', category: 'Beauty' },
            ].map((brand, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.03 }}
                className="bg-white p-4 rounded-xl border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all text-center group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:from-orange-200 group-hover:to-amber-200 transition-colors">
                  <span className="text-xl font-bold text-orange-600">{brand.name.charAt(0)}</span>
                </div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{brand.name}</h3>
                <p className="text-xs text-slate-500">{brand.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="process-section" className="py-16 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Our Process
            </h2>
            <p className="text-lg text-slate-600">
              A proven methodology for marketplace success
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="relative"
                data-testid={`process-step-${idx}`}
              >
                <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <ArrowRight className="text-primary" size={32} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="testimonials-section" className="py-16 md:py-24 lg:py-32 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Trusted by Growing <span className="text-gradient">Marketplace Brands</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Here's what our brand partners say about working with Obiquiste Solutions.
            </p>
          </div>

          <div className="relative overflow-hidden">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {testimonials.slice(currentTestimonial, currentTestimonial + 6).map((testimonial, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="group bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100 hover:border-primary/20"
                  data-testid={`testimonial-card-${idx}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    {testimonial.featured && (
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                        {testimonial.featured}
                      </span>
                    )}
                  </div>

                  <p className="text-slate-600 leading-relaxed mb-6 text-sm">
                    "{testimonial.text}"
                  </p>

                  <div className="pt-4 border-t border-slate-100">
                    <h4 className="font-heading font-bold text-lg text-slate-900">
                      {testimonial.brand}
                    </h4>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <div className="flex justify-center items-center mt-12 space-x-4">
              <button
                data-testid="testimonial-prev"
                onClick={() =>
                  setCurrentTestimonial((prev) =>
                    prev === 0 ? Math.max(0, testimonials.length - 6) : Math.max(0, prev - 3)
                  )
                }
                disabled={currentTestimonial === 0}
                className="p-3 bg-white border-2 border-slate-200 hover:border-primary hover:bg-primary/5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={24} className="text-slate-700" />
              </button>

              <div className="flex space-x-2">
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx * 3)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      Math.floor(currentTestimonial / 3) === idx
                        ? 'bg-primary w-8'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>

              <button
                data-testid="testimonial-next"
                onClick={() =>
                  setCurrentTestimonial((prev) =>
                    prev + 6 >= testimonials.length ? prev : Math.min(testimonials.length - 6, prev + 3)
                  )
                }
                disabled={currentTestimonial + 6 >= testimonials.length}
                className="p-3 bg-white border-2 border-slate-200 hover:border-primary hover:bg-primary/5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={24} className="text-slate-700" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="audit-form-section" className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-4">
                Get a Free Marketplace Growth Audit
              </h2>
              <p className="text-lg text-slate-600">
                Discover untapped opportunities to scale your e-commerce business
              </p>
            </div>
            <form
              onSubmit={handleAuditSubmit}
              data-testid="audit-form"
              className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    data-testid="audit-form-name"
                    value={auditForm.name}
                    onChange={(e) => setAuditForm({ ...auditForm, name: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="brand_name">Brand Name *</Label>
                  <Input
                    id="brand_name"
                    data-testid="audit-form-brand"
                    value={auditForm.brand_name}
                    onChange={(e) => setAuditForm({ ...auditForm, brand_name: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="audit-form-phone"
                    value={auditForm.phone}
                    onChange={(e) => setAuditForm({ ...auditForm, phone: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="audit-form-email"
                    value={auditForm.email}
                    onChange={(e) => setAuditForm({ ...auditForm, email: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="monthly_revenue">Monthly Revenue *</Label>
                  <Select
                    value={auditForm.monthly_revenue}
                    onValueChange={(value) =>
                      setAuditForm({ ...auditForm, monthly_revenue: value })
                    }
                  >
                    <SelectTrigger data-testid="audit-form-revenue" className="mt-2">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-5L">0 - 5 Lakhs</SelectItem>
                      <SelectItem value="5-10L">5 - 10 Lakhs</SelectItem>
                      <SelectItem value="10-25L">10 - 25 Lakhs</SelectItem>
                      <SelectItem value="25-50L">25 - 50 Lakhs</SelectItem>
                      <SelectItem value="50L+">50 Lakhs+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="platform">Platform Selling On *</Label>
                  <Select
                    value={auditForm.platform}
                    onValueChange={(value) => setAuditForm({ ...auditForm, platform: value })}
                  >
                    <SelectTrigger data-testid="audit-form-platform" className="mt-2">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Amazon">Amazon</SelectItem>
                      <SelectItem value="Flipkart">Flipkart</SelectItem>
                      <SelectItem value="Myntra">Myntra</SelectItem>
                      <SelectItem value="Meesho">Meesho</SelectItem>
                      <SelectItem value="Multiple">Multiple Platforms</SelectItem>
                      <SelectItem value="None">Not Selling Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="audit-form-submit"
                disabled={isSubmitting}
                className="w-full mt-6 bg-primary text-white hover:bg-primary-600 py-6 rounded-full text-base font-semibold"
              >
                {isSubmitting ? 'Submitting...' : 'Request Free Audit'}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
